"use client"

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
// Define locally to decouple from removed /search page
export interface SearchFilters {
  query: string
  role: string[]
  sectors: string[]
  skills: string[]
  experience: string
  location: string
  availability: string
  boardExperience: string[]
  remotePreference?: string
  salaryMin?: number
  salaryMax?: number
}
// import { useEffect, useRef } from 'react' - not needed without Clerk

interface SavedSearch {
  id: string
  name: string
  filters: SearchFilters
  createdAt: Date
  resultCount?: number
  synced?: boolean
}

interface SavedSearchStore {
  searches: SavedSearch[]
  addSearch: (name: string, filters: SearchFilters, resultCount?: number) => void
  removeSearch: (id: string) => void
  updateSearch: (id: string, updates: Partial<SavedSearch>) => void
  getSearch: (id: string) => SavedSearch | undefined
  clearSearches: () => void
  syncWithServer: () => Promise<void>
  loadFromServer: () => Promise<void>
  markAllSynced: () => void
}

const store = create<SavedSearchStore>()(
  persist(
    (set, get) => ({
      searches: [],
      
      addSearch: (name, filters, resultCount) => {
        const newSearch: SavedSearch = {
          id: `search-${Date.now()}`,
          name,
          filters,
          createdAt: new Date(),
          resultCount,
          synced: false
        }
        set((state) => ({
          searches: [...state.searches, newSearch]
        }))
      },
      
      removeSearch: (id) => set((state) => ({
        searches: state.searches.filter(s => s.id !== id)
      })),
      
      updateSearch: (id, updates) => set((state) => ({
        searches: state.searches.map(s => 
          s.id === id ? { ...s, ...updates } : s
        )
      })),
      
      getSearch: (id) => {
        return get().searches.find(s => s.id === id)
      },
      
      clearSearches: () => set({ searches: [] }),
      
      syncWithServer: async () => {
        const state = get()
        const unsyncedSearches = state.searches.filter(s => !s.synced)

        if (unsyncedSearches.length === 0) return

        try {
          // The server endpoint creates one saved search at a time
          for (const search of unsyncedSearches) {
            const response = await fetch('/api/v1/search/saved', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: search.name,
                // Server schema expects advanced filters; send what we have
                filters: search.filters,
              })
            })

            if (response.ok) {
              // Mark this specific search as synced
              set((current) => ({
                searches: current.searches.map((s) =>
                  s.id === search.id ? { ...s, synced: true } : s
                ),
              }))
            }
          }
        } catch (error) {
          console.error('Failed to sync saved searches:', error)
        }
      },
      
      loadFromServer: async () => {
        try {
          const response = await fetch('/api/v1/search/saved')
          if (response.ok) {
            const data = await response.json()
            if (data.data && Array.isArray(data.data)) {
              set({ 
                searches: data.data.map((s: any) => ({
                  ...s,
                  createdAt: new Date(s.createdAt),
                  synced: true
                }))
              })
            }
          }
        } catch (error) {
          console.error('Failed to load saved searches:', error)
        }
      },
      
      markAllSynced: () => set((state) => ({
        searches: state.searches.map(s => ({ ...s, synced: true }))
      }))
    }),
    {
      name: 'boardchampions-saved-searches'
    }
  )
)

export const useSavedSearches = () => {
  const storeState = store()
  
  // Since Clerk is not configured, we'll just use local storage
  // Server sync functionality is disabled until authentication is set up
  
  return storeState
}